// ─── Risk Dashboard Mini ─────────────────────────────────
// Floating mini panel in bottom-right corner showing:
//   - Total open positions count + breakdown by symbol
//   - Spread status per active symbol
//   - Overall workspace risk control status
//
// Auto-collapses into a compact badge when nothing is wrong.

import { useState, useMemo } from 'react';
import type { RiskEvaluation, RiskPosition } from '@/lib/chin-risk-controller';
import type { ChinBotSnapshot } from '@/lib/chin-bot-engine';
import { calcSpreadPips } from '@/lib/chin-risk-controller';

// ─── Types ────────────────────────────────────────────────

interface SpreadSnap {
  symbolId: number;
  symbolName: string;
  spreadPips: number | null;
  isHigh: boolean;
}

interface RiskDashboardProps {
  evaluation: RiskEvaluation | null;
  positions: RiskPosition[];
  engineState: ChinBotSnapshot | null;
  activeSymbolIds: number[];
  isLoading: boolean;
  onRefresh: () => void;
}

// ─── Helpers ──────────────────────────────────────────────

const SPREAD_HIGH_THRESHOLD = 3;

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Component ────────────────────────────────────────────

export default function RiskDashboard({
  evaluation,
  positions,
  engineState,
  activeSymbolIds,
  isLoading,
  onRefresh,
}: RiskDashboardProps) {
  const [expanded, setExpanded] = useState(false);

  // ─── Derived data ─────────────────────────────────────

  const totalPositions = positions.length;

  const spreadSnapshots: SpreadSnap[] = useMemo(() => {
    if (!engineState) return [];
    return activeSymbolIds.map((sid) => {
      const buf = engineState.symbols[sid];
      const name = buf?.symbolName || `#${sid}`;
      let pips: number | null = null;
      if (buf && buf.currentBid !== null && buf.currentAsk !== null) {
        const raw = buf.currentAsk - buf.currentBid;
        if (raw > 0) {
          const calculated = calcSpreadPips(buf.currentBid, buf.currentAsk, name, buf.pipPosition ?? null);
          pips = calculated;
        }
      }
      return {
        symbolId: sid,
        symbolName: name,
        spreadPips: pips,
        isHigh: pips !== null && pips > SPREAD_HIGH_THRESHOLD,
      };
    });
  }, [engineState, activeSymbolIds]);

  const hasHighSpread = spreadSnapshots.some((s) => s.isHigh);
  const maxSpread = spreadSnapshots.reduce((max, s) => {
    if (s.spreadPips !== null && s.spreadPips > max) return s.spreadPips;
    return max;
  }, 0);

  const blockedCount = activeSymbolIds.filter(
    (sid) => evaluation?.checks[sid]?.allowed === false && evaluation?.checks[sid]?.blockedBy !== 'none'
  ).length;

  // Overall risk status
  const riskStatus: { label: string; color: string; bg: string; dot: string } = useMemo(() => {
    if (!evaluation) return { label: 'Initializing', color: 'text-foreground-500', bg: 'bg-foreground-100', dot: 'bg-foreground-400' };
    if (evaluation.symbolsWithPositions.length > 0) {
      return { label: 'Single Symbol', color: 'text-amber-400', bg: 'bg-amber-500/10', dot: 'bg-amber-400' };
    }
    if (blockedCount > 0) {
      return { label: 'Blocked', color: 'text-red-400', bg: 'bg-red-500/10', dot: 'bg-red-400' };
    }
    if (hasHighSpread) {
      return { label: 'Spread Alert', color: 'text-amber-400', bg: 'bg-amber-500/10', dot: 'bg-amber-400' };
    }
    return { label: 'Clear', color: 'text-emerald-400', bg: 'bg-emerald-500/10', dot: 'bg-emerald-400' };
  }, [evaluation, blockedCount, hasHighSpread]);

  // Has anything worth showing?
  const hasIssues = totalPositions > 0 || hasHighSpread || blockedCount > 0;

  // ─── Render ────────────────────────────────────────────

  if (!evaluation && totalPositions === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Collapsed badge — always visible */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-xl border shadow-sm transition-all cursor-pointer',
          riskStatus.bg,
          expanded ? 'rounded-b-none' : ''
        )}
        title="Risk Dashboard"
      >
        {/* Status dot */}
        <span className={cn('w-2 h-2 rounded-full', riskStatus.dot, totalPositions > 0 && 'animate-pulse')}></span>

        {/* Compact info */}
        <div className="flex items-center gap-2 text-[10px] font-semibold">
          <span className={riskStatus.color}>{riskStatus.label}</span>
          {totalPositions > 0 && (
            <span className={cn(
              'px-1.5 py-0.5 rounded-full text-[9px] font-bold',
              totalPositions > 0
                ? 'bg-red-500/15 text-red-400'
                : 'bg-foreground-200/50 text-foreground-500'
            )}>
              {totalPositions} pos
            </span>
          )}
          {hasHighSpread && !evaluation?.symbolsWithPositions.length && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/15 text-amber-400">
              {maxSpread.toFixed(1)}p
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <i className={cn(
          'text-[10px] transition-transform',
          expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line',
          riskStatus.color
        )}></i>

        {/* Loading spinner */}
        {isLoading && (
          <div className="w-3 h-3 border-2 border-foreground-300 border-t-transparent rounded-full animate-spin"></div>
        )}
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="w-72 bg-background-50 border border-background-200/70 rounded-xl rounded-tr-none shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-background-200/70 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-background-200/70 flex items-center justify-center">
                <i className="ri-shield-check-line text-foreground-500 text-xs"></i>
              </div>
              <span className="text-[11px] font-heading font-semibold text-foreground-900">Risk Dashboard</span>
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className="w-6 h-6 rounded-md hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-50"
              title="Refresh positions"
            >
              <i className={`ri-refresh-line text-foreground-400 text-xs ${isLoading ? 'animate-spin' : ''}`}></i>
            </button>
          </div>

          {/* Body */}
          <div className="px-3 py-2.5 space-y-2.5 max-h-[320px] overflow-y-auto">

            {/* ─── Overall Status ─── */}
            <div className={cn('rounded-lg px-3 py-2 flex items-center gap-2.5', riskStatus.bg)}>
              <span className={cn('w-2 h-2 rounded-full shrink-0', riskStatus.dot, totalPositions > 0 && 'animate-pulse')}></span>
              <div className="flex-1 min-w-0">
                <span className={cn('text-[11px] font-semibold', riskStatus.color)}>{riskStatus.label}</span>
                {evaluation?.symbolsWithPositions.length ? (
                  <p className="text-[9px] text-foreground-500 mt-0.5">Only 1 symbol can trade at a time</p>
                ) : blockedCount > 0 ? (
                  <p className="text-[9px] text-foreground-500 mt-0.5">{blockedCount} symbol(s) blocked</p>
                ) : hasHighSpread ? (
                  <p className="text-[9px] text-foreground-500 mt-0.5">Spread exceeds 3 pips threshold</p>
                ) : (
                  <p className="text-[9px] text-foreground-500 mt-0.5">All risk checks passed</p>
                )}
              </div>
            </div>

            {/* ─── Positions Section ─── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-foreground-700 uppercase tracking-wide">Positions</span>
                <span className={cn(
                  'text-[10px] font-bold',
                  totalPositions > 0 ? 'text-red-400' : 'text-foreground-400'
                )}>
                  {totalPositions} total
                </span>
              </div>

              {totalPositions === 0 ? (
                <div className="text-[9px] text-foreground-400 italic py-1">No open positions</div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(evaluation?.positionCountBySymbol || {}).map(([sid, count]) => {
                    const symName = engineState?.symbols[Number(sid)]?.symbolName || `#${sid}`;
                    const symPositions = positions.filter((p) => p.symbolId === Number(sid));
                    const buyCount = symPositions.filter((p) => p.tradeSide === 'BUY').length;
                    const sellCount = symPositions.filter((p) => p.tradeSide === 'SELL').length;
                    const totalVol = symPositions.reduce((sum, p) => sum + p.volume, 0);

                    return (
                      <div key={sid} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-background-100/70">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[10px] font-mono font-semibold text-foreground-900 truncate">{symName}</span>
                          <span className="text-[9px] text-foreground-400">{totalVol.toFixed(2)}L</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {buyCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-500/15 text-emerald-400">{buyCount}B</span>
                          )}
                          {sellCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-rose-500/15 text-rose-400">{sellCount}S</span>
                          )}
                          <span className="text-[9px] font-semibold text-foreground-500">{count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ─── Spread Section ─── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-foreground-700 uppercase tracking-wide">Spread</span>
                <span className={cn(
                  'text-[10px] font-bold',
                  hasHighSpread ? 'text-amber-400' : 'text-emerald-400'
                )}>
                  {hasHighSpread ? 'Alert' : 'OK'}
                </span>
              </div>

              <div className="space-y-1">
                {spreadSnapshots.slice(0, 8).map((s) => (
                  <div key={s.symbolId} className="flex items-center justify-between px-2 py-1 rounded-md bg-background-100/70">
                    <span className="text-[10px] font-mono text-foreground-700 truncate max-w-[120px]">{s.symbolName}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={cn(
                        'text-[10px] font-semibold',
                        s.isHigh ? 'text-amber-400' : 'text-foreground-500',
                        s.spreadPips === null && 'text-foreground-300 italic'
                      )}>
                        {s.spreadPips !== null ? `${s.spreadPips.toFixed(1)}p` : '—'}
                      </span>
                      <span className={cn(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        s.isHigh ? 'bg-amber-400' : s.spreadPips !== null ? 'bg-emerald-400' : 'bg-foreground-300'
                      )}></span>
                    </div>
                  </div>
                ))}
                {spreadSnapshots.length > 8 && (
                  <div className="text-[9px] text-foreground-400 text-center">
                    +{spreadSnapshots.length - 8} more symbols
                  </div>
                )}
                {spreadSnapshots.length === 0 && (
                  <div className="text-[9px] text-foreground-400 italic py-1">Waiting for price data...</div>
                )}
              </div>
            </div>

            {/* ─── Blocked Symbols Section ─── */}
            {blockedCount > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold text-foreground-700 uppercase tracking-wide">Blocked</span>
                  <span className="text-[10px] font-bold text-red-400">{blockedCount} symbol(s)</span>
                </div>
                <div className="space-y-1">
                  {activeSymbolIds
                    .filter((sid) => evaluation?.checks[sid]?.allowed === false && evaluation?.checks[sid]?.blockedBy !== 'none')
                    .map((sid) => {
                      const chk = evaluation?.checks[sid];
                      const symName = engineState?.symbols[sid]?.symbolName || `#${sid}`;
                      return (
                        <div key={sid} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-red-500/5">
                          <span className="text-[10px] font-mono font-semibold text-foreground-800 truncate max-w-[100px]">{symName}</span>
                          <span className="text-[8px] font-medium text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-full shrink-0">
                            {chk?.blockedBy === 'spread' ? 'Spread' : chk?.blockedBy === 'maxOrders' ? 'Max Orders' : chk?.blockedBy === 'singleSymbol' ? 'Single' : chk?.blockedBy}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}